import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import { PieChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import { getColor } from 'helpers/utils';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import bg1 from 'assets/img/icons/spot-illustrations/corner-1.png';

import BasicECharts from 'components/common/BasicEChart';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import generateRandomHexColor from 'components/pages/sistema/randomColorGenerator';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  PieChart,
  CanvasRenderer
]);

const formatter = new Intl.NumberFormat("pt-BR", {
  style: "currency", 
  currency: "BRL"
});

const getOptions = (data, radius) => {
  const rawTotal = data.reduce((soma, item) => soma + item.value, 0);
  const total = formatter.format(rawTotal);
  
  return ({
  color: data.map(item => item.color),

  tooltip: {
    padding: [7, 10],
    transitionDuration: 0,
    backgroundColor: getColor('gray-100'),
    borderColor: getColor('gray-300'),
    textStyle: { color: getColor('dark') },
    borderWidth: 1,
    formatter: params =>
      `<strong>${params.data.name}:</strong> ${params.percent}%`
  },
  series: [
    {
      name: total,
      type: 'pie',
      radius,
      avoidLabelOverlap: false,
      emphasis: {
        scale: false
      },
      itemStyle: {
        borderWidth: 2,
        borderColor: getColor('gray-100')
      },
      label: {
        show: true,
        position: 'center',
        formatter: '{a}',
        fontSize: rawTotal < 1000000 ? 23 : 
                  rawTotal < 10000000 ? 19 : 16,
        color: getColor('dark')
      },
      data
    }
  ]
})};

const cardStyle = {
  backgroundPosition: "right",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover"
};

const rowStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-around"
};

const colStyle = {
  fontWeight: "bold",
  marginTop: "20px"
};

const legendColStyle = {
  display: "flex",
  marginTop: "20px",
  flexDirection: "column",
  justifyContent: "space-between",
};

const chartStyle = { width: '15rem', height: '15rem' };

const FormaPagamentoItem = React.memo(({ item, index, total }) => {
  const { name, value, color } = item;

  const percentage = ((value * 100) / total).toFixed(1);
  return (
    <Flex
      justifyContent="between"
      className={`fw-semi-bold fs--2 ${index == 0 && 'mt-3'}`}
    >
      <p className="mb-1 vendas-formapagamento-descricao">
        <FontAwesomeIcon
          icon="circle"
          className='me-2'
          color={color}
        />
        {name}
      </p>
      <p className="vendas-formapagamento-valor">
        {formatter.format(value)}
      </p>
      <p className="vendas-formapagamento-porcentagem">{percentage}%</p>
    </Flex>
  );
});

const VendaFormaPagamento = ({ data, radius }) => {
  const processedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.map((item) => ({
      name: item.formaPagamentoNome,
      value: item.total,
      color: generateRandomHexColor()
    }));
  }, [data]);

  const total = useMemo(() => data.reduce((acc, val) => val.total + acc, 0), [data]);

  const chartOptions = useMemo(() => getOptions(processedData, radius), [processedData, radius]);

  return (
    <Card className="h-md-100 dashboard-card-shadow"
      style={{
        ...cardStyle,
        backgroundImage: `url(${bg1})`
      }}
    >
      <Card.Header>
        <h4 className="mt-2 ms-2">Vendas por Forma de Pagamento</h4>
      </Card.Header>
      <Card.Body>
        <Row className="mt-3" style={rowStyle}>
            <Col xs="auto" style={colStyle}>
              <div className="ps-0">
                <BasicECharts
                  echarts={echarts}
                  options={chartOptions}
                  style={chartStyle}
                />
              </div>
            </Col>
        </Row>
        <Row className='mt-3'>
          <Col xxl className="pe-2" style={legendColStyle}>
            {processedData.map((item, index) => (
              <FormaPagamentoItem
                item={item}
                key={index}
                index={index}
                total={total}
              />
            ))}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

FormaPagamentoItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    value: PropTypes.number.isRequired
  }),
  index: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired
};

VendaFormaPagamento.propTypes = {
  data: PropTypes.array.isRequired,
  radius: PropTypes.array.isRequired
};

export default React.memo(VendaFormaPagamento);
